import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Grid,
  Avatar,
  Fade,
  Skeleton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  alpha,
  useTheme,
  Alert,
  AlertTitle,
} from '@mui/material';
import { FormControlLabel, Switch } from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  Policy as PolicyIcon,
  MoreVert,
  Business,
  CalendarMonth,
  LunchDining,
  InfoOutlined,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import leaveService, { LeaveType, LeavePolicy, LeaveAllocation } from '../../services/leaveService';
import { roleService } from '../../services/roleService';
import ConfirmationDialog from '../common/ConfirmationDialog';

// Validation schema
const leavePolicyFormSchema = yup.object({
  name: yup.string().required('Policy name is required').max(100, 'Name must be 100 characters or less'),
  description: yup.string().max(500, 'Description must be 500 characters or less'),
  cycleStartMonth: yup.number().required().min(1).max(12),
});

interface Role {
  _id: string;
  name: string;
}

interface AllocationFormData {
  leaveTypeId: string;
  annualQuota: number;
  carryForwardLimit: number;
  maxConsecutiveDays: number;
}

interface PolicyFormData {
  name: string;
  description: string;
  cycleStartMonth: number;
  roleIds: string[];
  enableSandwichRule: boolean;
  leaveAllocations: AllocationFormData[];
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const LeavePolicySettings: React.FC = () => {
  const theme = useTheme();
  const { canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [policies, setPolicies] = useState<LeavePolicy[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<LeavePolicy | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<LeavePolicy | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const formik = useFormik<PolicyFormData>({
    initialValues: {
      name: '',
      description: '',
      cycleStartMonth: 1,
      roleIds: [],
      enableSandwichRule: false,
      leaveAllocations: [],
    },
    validationSchema: leavePolicyFormSchema,
    onSubmit: async (values) => {
      try {
        const payload = {
          name: values.name,
          description: values.description,
          cycleStartMonth: values.cycleStartMonth,
          roleIds: values.roleIds,
          enableSandwichRule: values.enableSandwichRule,
          leaveAllocations: values.leaveAllocations.filter(a => a.annualQuota > 0),
        };

        if (editingPolicy) {
          await leaveService.updateLeavePolicy(editingPolicy._id, payload);
          enqueueSnackbar('Leave policy updated successfully', { variant: 'success' });
        } else {
          await leaveService.createLeavePolicy(payload);
          enqueueSnackbar('Leave policy created successfully', { variant: 'success' });
        }
        handleCloseDialog();
        loadData();
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to save leave policy';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    },
  });

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [policiesData, leaveTypesData, rolesData] = await Promise.all([
        leaveService.getLeavePolicies(),
        leaveService.getLeaveTypes(),
        roleService.getRolesCatalog(),
      ]);
      setPolicies(policiesData);
      setLeaveTypes(leaveTypesData);
      setRoles(rolesData || []);
    } catch (error) {
      enqueueSnackbar('Failed to fetch data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Initialize allocations when dialog opens
  const initializeAllocations = useCallback((existingAllocations?: LeaveAllocation[]) => {
    const allocations: AllocationFormData[] = leaveTypes.map(lt => {
      const existing = existingAllocations?.find(
        a => (typeof a.leaveTypeId === 'object' ? a.leaveTypeId._id : a.leaveTypeId) === lt._id
      );
      return {
        leaveTypeId: lt._id,
        annualQuota: existing?.annualQuota || 0,
        carryForwardLimit: existing?.carryForwardLimit || 0,
        maxConsecutiveDays: existing?.maxConsecutiveDays || 0,
      };
    });
    formik.setFieldValue('leaveAllocations', allocations);
  }, [leaveTypes, formik]);

  // Handlers
  const handleOpenDialog = (policy?: LeavePolicy) => {
    if (policy) {
      setEditingPolicy(policy);
      const roleIdValues = policy.roleIds.map(r => typeof r === 'object' ? r._id : r);
      formik.setValues({
        name: policy.name,
        description: policy.description || '',
        cycleStartMonth: policy.cycleStartMonth,
        roleIds: roleIdValues,
        enableSandwichRule: policy.enableSandwichRule || false,
        leaveAllocations: [],
      });
      setTimeout(() => initializeAllocations(policy.leaveAllocations), 0);
    } else {
      setEditingPolicy(null);
      formik.resetForm();
      initializeAllocations();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPolicy(null);
    formik.resetForm();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, policy: LeavePolicy) => {
    setAnchorEl(event.currentTarget);
    setSelectedPolicy(policy);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPolicy(null);
  };

  const handleEdit = () => {
    if (selectedPolicy) {
      handleOpenDialog(selectedPolicy);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (selectedPolicy) {
      try {
        await leaveService.deleteLeavePolicy(selectedPolicy._id);
        enqueueSnackbar('Leave policy deleted successfully', { variant: 'success' });
        setDeleteDialogOpen(false);
        setSelectedPolicy(null);
        loadData();
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to delete leave policy';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleAllocationChange = (leaveTypeId: string, field: keyof AllocationFormData, value: number) => {
    const allocations = [...formik.values.leaveAllocations];
    const index = allocations.findIndex(a => a.leaveTypeId === leaveTypeId);
    if (index !== -1) {
      allocations[index] = { ...allocations[index], [field]: value };
      formik.setFieldValue('leaveAllocations', allocations);
    }
  };

  const canManagePolicies = canWrite('settings_leave_types');

  const filteredPolicies = policies.filter(policy =>
    policy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (policy.description && policy.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search leave policies..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
        </Box>
        
        {canManagePolicies && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            disabled={leaveTypes.length === 0}
          >
            Add Leave Policy
          </Button>
        )}
      </Box>

      {/* Info banner if no leave types */}
      {leaveTypes.length === 0 && !loading && (
        <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.warning.main, 0.1), border: `1px solid ${theme.palette.warning.main}` }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="body2" color="warning.main">
              Please create leave types first before setting up leave policies.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Policies List */}
      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3].map((item) => (
            <Grid size={{ xs: 12, lg: 6 }} key={item}>
              <Card>
                <CardContent>
                  <Skeleton variant="circular" width={56} height={56} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="80%" height={28} />
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="rectangular" height={120} sx={{ mt: 2 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : filteredPolicies.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <PolicyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Leave Policies Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm ? 'No policies match your search criteria.' : 'Create your first leave policy to assign leave quotas to user roles.'}
            </Typography>
            {canManagePolicies && !searchTerm && leaveTypes.length > 0 && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
              >
                Create First Policy
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2.5}>
          {filteredPolicies.map((policy, index) => (
            <Grid size={{ xs: 12, lg: 6 }} key={policy._id}>
              <Fade in={true} timeout={300 + index * 100}>
                <Card 
                  sx={{ 
                    transition: 'all 0.2s ease-in-out',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                      borderColor: 'primary.main',
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    {/* Header Row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          width: 40,
                          height: 40,
                        }}
                      >
                        <PolicyIcon sx={{ fontSize: 20 }} />
                      </Avatar>
                      
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                            {policy.name}
                          </Typography>
                          {policy.enableSandwichRule && (
                            <Chip
                              icon={<LunchDining sx={{ fontSize: 14 }} />}
                              label="Sandwich"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                color: 'success.dark',
                                fontWeight: 600,
                                '& .MuiChip-icon': {
                                  color: 'success.main',
                                  fontSize: 14
                                }
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}>
                          {policy.description || 'No description provided'}
                        </Typography>
                      </Box>

                      {canManagePolicies && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, policy)}
                          sx={{ mt: -0.5, mr: -0.5 }}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      )}
                    </Box>

                    {/* Info Grid */}
                    <Box sx={{ display: 'grid', gap: 1.5 }}>
                      {/* Cycle Info */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarMonth sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          Cycle starts {MONTH_NAMES[policy.cycleStartMonth - 1]}
                        </Typography>
                      </Box>

                      {/* Assigned Roles */}
                      {policy.roleIds.length > 0 && (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Business sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                              ROLES
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {policy.roleIds.map((role, idx) => (
                              <Chip
                                key={idx}
                                label={typeof role === 'object' ? role.name : role}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.75rem',
                                  bgcolor: alpha(theme.palette.info.main, 0.08),
                                  color: 'info.dark',
                                  fontWeight: 500,
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Leave Quotas */}
                      {policy.leaveAllocations.filter(a => a.annualQuota > 0).length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                            LEAVE QUOTAS
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {policy.leaveAllocations.filter(a => a.annualQuota > 0).map((allocation, idx) => {
                              const lt = typeof allocation.leaveTypeId === 'object' ? allocation.leaveTypeId : null;
                              return (
                                <Chip
                                  key={idx}
                                  label={`${lt?.name || 'Unknown'}: ${allocation.annualQuota}d`}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.75rem',
                                    bgcolor: alpha(lt?.color || theme.palette.grey[500], 0.1),
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    borderLeft: `3px solid ${lt?.color || theme.palette.grey[500]}`,
                                    pl: 1,
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {canManagePolicies && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Policy</ListItemText>
          </MenuItem>
        )}
        {canManagePolicies && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete Policy</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedPolicy(null);
        }}
        onConfirm={handleDelete}
        title="Delete Leave Policy?"
        message={`Are you sure you want to delete policy "${selectedPolicy?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />

      {/* Add/Edit Dialog - keeping original implementation */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPolicy ? 'Edit Leave Policy' : 'Create Leave Policy'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={formik.handleSubmit} sx={{ pt: 1 }}>
            {editingPolicy && (
              <Alert
                severity="info"
                variant="outlined"
                icon={<InfoOutlined fontSize="small" />}
                sx={{
                  mb: 2.5,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.info.main, 0.04),
                  '& .MuiAlert-message': { py: 0.25 },
                }}
              >
                <AlertTitle sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.25 }}>
                  Changes apply going forward
                </AlertTitle>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
                  Updates to quotas, sandwich rule, or cycle start month apply to leaves submitted from now on.
                  Existing approved leaves keep their original day count.
                </Typography>
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  name="name"
                  label="Policy Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  placeholder="e.g., Standard Policy, Probation Policy"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  multiple
                  options={roles}
                  getOptionLabel={(option) => option.name}
                  getOptionDisabled={(option) => {
                    const assignedPolicy = policies.find(p => 
                      p._id !== editingPolicy?._id && 
                      p.roleIds.some(r => (typeof r === 'object' ? r._id : r) === option._id)
                    );
                    return !!assignedPolicy;
                  }}
                  value={roles.filter(r => formik.values.roleIds.includes(r._id))}
                  onChange={(_, newValue) => {
                    formik.setFieldValue('roleIds', newValue.map(r => r._id));
                  }}
                  renderOption={(props, option) => {
                    const assignedPolicy = policies.find(p => 
                      p._id !== editingPolicy?._id && 
                      p.roleIds.some(r => (typeof r === 'object' ? r._id : r) === option._id)
                    );
                    return (
                      <li {...props}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                          <Typography>{option.name}</Typography>
                          {assignedPolicy && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              (in {assignedPolicy.name})
                            </Typography>
                          )}
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Assign to Roles" placeholder="Select roles" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip label={option.name} size="small" {...getTagProps({ index })} />
                    ))
                  }
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  name="description"
                  label="Description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  multiline
                  rows={2}
                  placeholder="Optional description for this policy"
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>

              {/* Sandwich Rule Toggle */}
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.enableSandwichRule}
                      onChange={formik.handleChange}
                      name="enableSandwichRule"
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Enable Sandwich Rule</Typography>
                      <Typography variant="caption" color="text.secondary">
                        If enabled, holidays/weekends falling within a leave period will be counted as leave days.
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              {/* Cycle Configuration */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
                  Leave Cycle Start Month
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  When should the leave year start? (e.g., January for calendar year, April for fiscal year)
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Start Month</InputLabel>
                  <Select
                    value={formik.values.cycleStartMonth}
                    label="Start Month"
                    onChange={(e) => formik.setFieldValue('cycleStartMonth', e.target.value)}
                  >
                    {MONTH_NAMES.map((month, idx) => (
                      <MenuItem key={idx} value={idx + 1}>{month}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Leave Allocations */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                  Leave Quotas
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Set the number of days allowed for each leave type per year
                </Typography>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell>Leave Type</TableCell>
                        <TableCell align="center" sx={{ width: 100 }}>Annual Quota</TableCell>
                        <TableCell align="center" sx={{ width: 120 }}>Carry Forward Limit</TableCell>
                        <TableCell align="center" sx={{ width: 130 }}>Max Consecutive Days</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formik.values.leaveAllocations.map((allocation) => {
                        const leaveType = leaveTypes.find(lt => lt._id === allocation.leaveTypeId);
                        if (!leaveType) return null;
                        return (
                          <TableRow key={allocation.leaveTypeId}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: leaveType.color }} />
                                <Typography variant="body2">{leaveType.name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small"
                                type="number"
                                value={allocation.annualQuota}
                                onChange={(e) => handleAllocationChange(allocation.leaveTypeId, 'annualQuota', parseInt(e.target.value) || 0)}
                                inputProps={{ min: 0, max: 365, style: { textAlign: 'center' } }}
                                sx={{ width: 70 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small"
                                type="number"
                                value={allocation.carryForwardLimit}
                                onChange={(e) => handleAllocationChange(allocation.leaveTypeId, 'carryForwardLimit', parseInt(e.target.value) || 0)}
                                inputProps={{ min: 0, max: 365, style: { textAlign: 'center' } }}
                                sx={{ width: 70 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small"
                                type="number"
                                value={allocation.maxConsecutiveDays}
                                onChange={(e) => handleAllocationChange(allocation.leaveTypeId, 'maxConsecutiveDays', parseInt(e.target.value) || 0)}
                                inputProps={{ min: 0, max: 365, style: { textAlign: 'center' } }}
                                sx={{ width: 70 }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={() => formik.handleSubmit()} 
            variant="contained" 
            disabled={formik.isSubmitting}
          >
            {editingPolicy ? 'Update Policy' : 'Create Policy'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeavePolicySettings;
